import * as React from "react";
import * as _ from "lodash";
import * as yup from "yup";
import { useMappedState } from "redux-react-hook";

import {
  ITerminology,
  ITerminologyItem
} from "src/library/utils/string-manipulations/tokenize-string";
import { FilterParams } from "src/library/utils";
import { Text } from "src/library/components/Text";
import {
  Input,
  Select
} from "src/library/components/Forms";
import DeliveryDaysPicker from "src/core/shared/components/DeliveryDaysPicker";
import { deliveryRoutesApi } from "src/core/shared/api/deliveryRoutes.api";
import {
  InheritedSettingNameType,
  IInheritedSetting,
  OrderedInheritedSettings,
  IDrop,
  IDeliveryRoute
} from "src/core/drops/api/drops.api";
import { ICustomer } from "src/core/customers/api/customers.api";
import { IStore } from "src/core/shared/store";
import { useCurrentUser } from "src/core/users/hooks";
import MinimumOrderQuantitySetting from "src/pages/Admin/Settings/MinimumOrderQuantity/MinimumOrderQuantitySetting";
import MinimumOrderQuantityDisplayOnly from "src/pages/Admin/Settings/MinimumOrderQuantity/MinimumOrderQuantityDisplayOnly";

import { OrderDataRequirements } from "./Inherited-settings/OrderDataRequirements";
import { TimePicker } from "./Date/TimePicker";

export type IFormFieldValues = { [P in Exclude<InheritedSettingNameType, "delivery_week_leadtimes" | "order_data_requirements" | "delivery_cutoff" | "point_of_contact" | "credit_request_auto_approval">]: IFormValue } & {
  delivery_week_leadtimes?: IFormValue;
  order_data_requirements?: IFormValue;
};

export interface IFormValue {
  name: Exclude<InheritedSettingNameType, "delivery_cutoff" | "point_of_contact">;
  inherited: boolean;
  inheritedValue?: IInheritedSetting<any>["value"];
  value?: IInheritedSetting<any>["value"];
  label: string | number | React.ReactNode;
  Description?: JSX.Element;
  hidden?: boolean;
  SettingElement: (setFieldValue: any, setting: any, handleBlur: any, disabled?: boolean) => React.ReactNode;
}

const useCascadeSettings = (
  terminology: ITerminology,
  scope: ITerminologyItem,
  entity: ICustomer | IDrop | null,
  inheritedValues?: OrderedInheritedSettings<IInheritedSetting<any>>,
  overrideValues?: OrderedInheritedSettings<IInheritedSetting<any>>
): [IFormFieldValues | null, (settings: IFormFieldValues | null) => void] => {
  const [ getDeliveryDaysResponse, getDeliveryDays ] = deliveryRoutesApi<IDeliveryRoute[]>("GET_DELIVERY_ROUTES");
  const [ settings, setSettings ] = React.useState<IFormFieldValues | null>(null);
  const { installedModules } = useMappedState(({ global }: IStore) => ({ installedModules: global.settings?.installedModules || [] }));
  const [ { currentRole } ] = useCurrentUser();

  // On mount
  React.useEffect(() => {
    getDeliveryDays<FilterParams<IDeliveryRoute>>({ type: "global" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On getDeliveryDaysResponse change
  React.useEffect(() => {
    // Set the settings state to null if there is no values to base logic off of
    if (!(getDeliveryDaysResponse.data && overrideValues && inheritedValues && inheritedValues.delivery_cutoff)) {
      return setSettings(null);
    }

    // Update the settings state
    setSettings({
      minimum_order_quantity: {
        name: "minimum_order_quantity",
        inherited: overrideValues && overrideValues.minimum_order_quantity ? false : true,
        inheritedValue: inheritedValues && inheritedValues.minimum_order_quantity && inheritedValues.minimum_order_quantity.value,
        label: inheritedValues && inheritedValues.minimum_order_quantity && inheritedValues.minimum_order_quantity.value ? (
          <MinimumOrderQuantityDisplayOnly settingValue={inheritedValues.minimum_order_quantity.value} />
        ) : "",
        value:
            overrideValues && overrideValues.minimum_order_quantity ? overrideValues && overrideValues.minimum_order_quantity.value : { quantity: 0 },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`${scope.singular.text} minimum ${terminology.order.singular.text} quantity`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This adds validation on ${
                terminology.order.plural.text
              } to stop low quantity ${
                terminology.order.plural.text
              } being placed`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => (
          <MinimumOrderQuantitySetting
            setting={setting}
            canAmend={!disabled}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
            inherited={false}
          />
        )
      },
      minimum_order_value: {
        name: "minimum_order_value",
        inherited: overrideValues && overrideValues.minimum_order_value ? false : true,
        label: inheritedValues && inheritedValues.minimum_order_value && inheritedValues.minimum_order_value.value.value,
        inheritedValue: inheritedValues && inheritedValues.minimum_order_value && inheritedValues.minimum_order_value.value,
        value: overrideValues && overrideValues.minimum_order_value ? overrideValues && overrideValues.minimum_order_value.value : { value: 0 },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`${scope.singular.text} minimum ${terminology.order.singular.text} value`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This adds validation on ${
                terminology.order.plural.text
              } to stop low value ${
                terminology.order.plural.text
              } being placed`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <Input
              value={setting.value.value}
              className="input"
              name={setting.name}
              inputType="number"
              autoHideLabels
              disabled={disabled}
              label={`${scope.singular} minimum order value`}
              handleChange={inputProps => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { value: inputProps.currentTarget.value }
                });
              }}
              handleBlur={handleBlur}
            />
          );
        }
      },
      one_daily_delivery: {
        name: "one_daily_delivery",
        inherited: overrideValues && overrideValues.one_daily_delivery ? false : true,
        label: inheritedValues && inheritedValues.one_daily_delivery && inheritedValues.one_daily_delivery.value.value ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.one_daily_delivery && inheritedValues.one_daily_delivery.value,
        value: overrideValues && overrideValues.one_daily_delivery ? overrideValues && overrideValues.one_daily_delivery.value : { value: false },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`One ${terminology.order.singular.text} per day`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This ensures that a maximum of one ${terminology.order.singular.text} is placed per day`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <>
              <Select
                value={setting.value.value}
                className="select-box"
                name={setting.name}
                autoHideLabels
                label="Single daily orders only"
                disabled={disabled}
                options={[
                  {
                    text: "Yes",
                    value: "true"
                  },
                  {
                    text: "No",
                    value: "false"
                  }
                ]}
                handleChange={selectProps => {
                  setFieldValue(setting.name, {
                    ...setting,
                    inherited: false,
                    value: { value: selectProps.currentTarget.value === "true" ? true : false }
                  });
                }}
                handleBlur={handleBlur}
              />
            </>
          );
        }
      },
      order_data_requirements: {
        name: "order_data_requirements",
        inherited: overrideValues && overrideValues.order_data_requirements ? false : true,
        label: inheritedValues?.order_data_requirements?.value?.orderPONumber === true || inheritedValues?.order_data_requirements?.value?.orderPONumber?.required ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.order_data_requirements && inheritedValues.order_data_requirements.value,
        value:
            overrideValues && overrideValues.order_data_requirements ?
              overrideValues && overrideValues.order_data_requirements.value :
              inheritedValues.order_data_requirements?.value,
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Require a PO number
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This adds validation on ${terminology.order.plural.text} to prevent ${terminology.order.plural.text} being placed without a PO number`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          const settingValue = (setting as IInheritedSetting<"order_data_requirements">).value.orderPONumber;

          return (
            <>
              <Select
                value={`${(typeof settingValue === "boolean" ? settingValue : settingValue.required)}`}
                className="select-box"
                name={setting.name}
                autoHideLabels
                label="Required PO number"
                disabled={disabled}
                options={[
                  {
                    text: "Yes",
                    value: "true"
                  },
                  {
                    text: "No",
                    value: "false"
                  }
                ]}
                handleChange={selectProps => {
                  setFieldValue(setting.name, {
                    ...setting,
                    inherited: false,
                    value: {
                      orderPONumber: typeof settingValue === "boolean" ? (selectProps.currentTarget.value === "true" ? true : false) : {
                        ...settingValue,
                        required: selectProps.currentTarget.value === "true" ? true : false
                      }
                    }
                  });
                }}
                handleBlur={handleBlur}
              />

              <OrderDataRequirements
                settingValue={setting.value}
                terminology={terminology}
                setSettingValue={value => setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value
                })}
              />
            </>
          );
        }
      },
      price_visible: {
        name: "price_visible",
        inherited: overrideValues && overrideValues.price_visible ? false : true,
        label: inheritedValues && inheritedValues.price_visible && inheritedValues.price_visible.value.visible ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.price_visible && inheritedValues.price_visible.value,
        value: overrideValues && overrideValues.price_visible ? overrideValues && overrideValues.price_visible.value : { visible: false },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Display prices
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets the visibility of prices on the platform
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <Select
              value={setting.value.visible}
              className="select-box"
              name={setting.name}
              autoHideLabels
              label="This sets the visibility of prices on the platform"
              disabled={disabled}
              options={[
                {
                  text: "Yes",
                  value: "true"
                },
                {
                  text: "No",
                  value: "false"
                }
              ]}
              handleChange={selectProps => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { visible: selectProps.currentTarget.value === "true" ? true : false }
                });
              }}
              handleBlur={handleBlur}
            />
          );
        }
      },
      require_payment_method: {
        name: "require_payment_method",
        inherited: overrideValues && overrideValues.require_payment_method ? false : true,
        label: inheritedValues && inheritedValues.require_payment_method && inheritedValues.require_payment_method.value.required ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.require_payment_method && inheritedValues.require_payment_method.value,
        value: overrideValues && overrideValues.require_payment_method ? overrideValues && overrideValues.require_payment_method.value : { required: false },
        hidden: !installedModules.includes("payment"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Require payment method
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets whether a payment method is required by the ${terminology.customer.singular.text} to create an order.`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <Select
              value={setting.value.required}
              className="select-box"
              name={setting.name}
              autoHideLabels
              disabled={disabled}
              label={`A payment method is required by the ${terminology.customer.singular.text}`}
              options={[
                {
                  text: "Yes",
                  value: "true"
                },
                {
                  text: "No",
                  value: "false"
                }
              ]}
              handleChange={selectProps => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { required: selectProps.currentTarget.value === "true" }
                });
              }}
              handleBlur={handleBlur}
            />
          );
        }
      },
      order_mode: {
        name: "order_mode",
        inherited: overrideValues && overrideValues.order_mode ? false : true,
        inheritedValue: inheritedValues && inheritedValues.order_mode && inheritedValues.order_mode.value,
        label: inheritedValues && inheritedValues.order_mode && inheritedValues.order_mode.value.allow,
        value: overrideValues && overrideValues.order_mode ? overrideValues.order_mode.value : { allow: "DL" },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`${terminology.order.singular.text}ing Mode`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`Select how your ${terminology.customer.plural.text} can make ${
                terminology.order.plural.text
              }. Made to order products will not be visible on date-led orders.`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => (
          <Select
            value={setting.value.allow}
            className="select-box"
            name={setting.name}
            autoHideLabels
            label={`${scope.singular} Mode`}
            disabled={disabled}
            options={[
              {
                value: "DL",
                text: "Date led ordering only",
                checked: setting.value.allow === "DL"
              },
              {
                value: "MTO",
                text: "Made to order only",
                checked: setting.value.allow === "MTO"
              },
              {
                value: "ANY",
                text: "Both made to order and date led ordering",
                checked: setting.value.allow === "ANY"
              }
            ]}
            handleChange={selectProps => {
              setFieldValue(setting.name, {
                ...setting,
                inherited: false,
                value: { allow: selectProps.currentTarget.value }
              });
            }}
            handleBlur={handleBlur}
          />
        )
      },
      allow_substitutions: {
        name: "allow_substitutions",
        inherited: overrideValues && overrideValues.allow_substitutions ? false : true,
        label: inheritedValues && inheritedValues.allow_substitutions && inheritedValues.allow_substitutions.value.allowed ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.allow_substitutions && inheritedValues.allow_substitutions.value,
        value: overrideValues && overrideValues.allow_substitutions ? overrideValues && overrideValues.allow_substitutions.value : { allowed: false },
        hidden: !installedModules.includes("substitution"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow substitutions
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets whether substitutions are allowed by a ${terminology.customer.singular.text}.`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <Select
              value={setting.value.allowed}
              className="select-box"
              name={setting.name}
              autoHideLabels
              disabled={disabled}
              label={`The ${terminology.customer.singular.text} allows substitutions to their orders`}
              options={[
                {
                  text: "Yes",
                  value: "true"
                },
                {
                  text: "No",
                  value: "false"
                }
              ]}
              handleChange={selectProps => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { allowed: selectProps.currentTarget.value === "true" }
                });
              }}
              handleBlur={handleBlur}
            />
          );
        }
      },
      allow_holidays: {
        name: "allow_holidays",
        inherited: overrideValues && overrideValues.allow_holidays ? false : true,
        label: inheritedValues && inheritedValues.allow_holidays && inheritedValues.allow_holidays.value.allowed ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.allow_holidays && inheritedValues.allow_holidays.value,
        value: overrideValues && overrideValues.allow_holidays ? overrideValues && overrideValues.allow_holidays.value : { allowed: false },
        hidden: !installedModules.includes("shutdown"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow Holidays
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether customers can setup their own holiday schedules to block orders for certain date ranges
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <Select
              value={setting.value.allowed}
              className="select-box"
              name={setting.name}
              autoHideLabels
              disabled={disabled}
              label={`The ${terminology.customer.singular.text} is allowed to create holiday schedules to block orders`}
              options={[
                {
                  text: "Yes",
                  value: "true"
                },
                {
                  text: "No",
                  value: "false"
                }
              ]}
              handleChange={selectProps => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { allowed: selectProps.currentTarget.value === "true" }
                });
              }}
              handleBlur={handleBlur}
            />
          );
        }
      },
      allow_order_customer_comments: {
        name: "allow_order_customer_comments",
        inherited: overrideValues && overrideValues.allow_order_customer_comments ? false : true,
        label: inheritedValues && inheritedValues.allow_order_customer_comments && inheritedValues.allow_order_customer_comments.value.allowed ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.allow_order_customer_comments && inheritedValues.allow_order_customer_comments.value,
        value: overrideValues && overrideValues.allow_order_customer_comments ? overrideValues && overrideValues.allow_order_customer_comments.value : { allowed: false },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow Order Comments
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether customers can leave comments on an order and which admin user groups are notified
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <Select
              value={setting.value.allowed}
              className="select-box"
              name={setting.name}
              autoHideLabels
              disabled={disabled}
              label={`${terminology.customer.plural.text} allow order comments`}
              options={[
                {
                  text: "Yes",
                  value: "true"
                },
                {
                  text: "No",
                  value: "false"
                }
              ]}
              handleChange={selectProps => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { allowed: selectProps.currentTarget.value === "true" }
                });
              }}
              handleBlur={handleBlur}
            />
          );
        }
      },
      allow_delivery_time: {
        name: "allow_delivery_time",
        inherited: overrideValues && overrideValues.allow_delivery_time ? false : true,
        label: inheritedValues && inheritedValues.allow_delivery_time && inheritedValues.allow_delivery_time.value.allowed ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.allow_delivery_time && inheritedValues.allow_delivery_time.value,
        value: overrideValues && overrideValues.allow_delivery_time ? overrideValues && overrideValues.allow_delivery_time.value : { allowed: false },
        hidden: !installedModules.includes("odoo"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow Requested Order Delivery Time
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether customers can request a time of day for when the order is delivered
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <>
              <Select
                value={setting.value.allowed}
                className="select-box"
                name={setting.name}
                autoHideLabels
                disabled={disabled}
                label={`${terminology.customer.plural.text} allow requested delivery time`}
                options={[
                  {
                    text: "Yes",
                    value: "true"
                  },
                  {
                    text: "No",
                    value: "false"
                  }
                ]}
                handleChange={selectProps => {
                  setFieldValue(setting.name, {
                    ...setting,
                    inherited: false,
                    value: { allowed: selectProps.currentTarget.value === "true" }
                  });
                }}
                handleBlur={handleBlur}
              />

              {setting.value.allowed && (
                <TimePicker
                  value={setting.value.defaultTime}
                  name="Default Time"
                  timezone="GMT"
                  label="Default Time"
                  setTime={value => {
                    setFieldValue(setting.name, {
                      ...setting,
                      value: {
                        ...setting.value,
                        defaultTime: value ?? null
                      }

                    });
                  }}
                />
              )}
            </>
          );
        }
      },

      allow_waste_recording: {
        name: "allow_waste_recording",
        inherited: overrideValues && overrideValues.allow_waste_recording ? false : true,
        label: inheritedValues && inheritedValues.allow_waste_recording && inheritedValues.allow_waste_recording.value.allowed ? "yes" : "no",
        inheritedValue: inheritedValues && inheritedValues.allow_waste_recording && inheritedValues.allow_waste_recording.value,
        value: overrideValues && overrideValues.allow_waste_recording ? overrideValues && overrideValues.allow_waste_recording.value : { allowed: false },
        hidden: !installedModules.includes("waste"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow waste recording
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets whether waste recording is allowed by a ${terminology.customer.singular.text}.`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur, disabled) => {
          return (
            <Select
              value={setting.value.allowed}
              className="select-box"
              name={setting.name}
              autoHideLabels
              disabled={disabled}
              label={`The ${terminology.customer.singular.text} is allowed to record waste on their orders`}
              options={[
                {
                  text: "Yes",
                  value: "true"
                },
                {
                  text: "No",
                  value: "false"
                }
              ]}
              handleChange={selectProps => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { allowed: selectProps.currentTarget.value === "true" }
                });
              }}
              handleBlur={handleBlur}
            />
          );
        }
      },
      delivery_route: {
        name: "delivery_route",
        inherited: overrideValues && overrideValues.delivery_route ? false : true,
        label:
            inheritedValues && inheritedValues.delivery_route && inheritedValues.delivery_route.deliveryRoute ?
              _.reduce(
                _.keys(inheritedValues.delivery_route.deliveryRoute.deliveryDays),
                (current: string, day: string) => {
                  if (day) {
                    return `${current} ${day},`;
                  }

                  return current;
                },
                ""
              ) :
              "",
        inheritedValue: inheritedValues && inheritedValues.delivery_route && inheritedValues.delivery_route.deliveryRoute,
        value:
            overrideValues && overrideValues.delivery_route ?
              overrideValues && overrideValues.delivery_route.deliveryRoute :
              inheritedValues && inheritedValues.delivery_route && inheritedValues.delivery_route.deliveryRoute,
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allowed delivery days
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`Controls which days ${scope.plural.text} can create ${terminology.delivery.plural.text} for`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, _handleChange, disabled) => {
          if (getDeliveryDaysResponse.data && inheritedValues && inheritedValues.delivery_route && inheritedValues.delivery_route.deliveryRoute) {
            return (
              <DeliveryDaysPicker
                inherited={inheritedValues.delivery_route.deliveryRoute}
                value={setting.value}
                type={scope.singular.text === "customer" ? "customer" : "branch"}
                deliveryRoutes={getDeliveryDaysResponse.data}
                disabled={disabled}
                handleChange={deliveryRoute => {
                  if (entity && deliveryRoute) {
                    deliveryRoute.entityId = entity.id;
                  }

                  setFieldValue(setting.name, {
                    ...setting,
                    inherited: false,
                    value: deliveryRoute
                  });
                }}
              />
            );
          }

          return null;
        }
      }
    });
  }, [
    inheritedValues,
    overrideValues,
    getDeliveryDaysResponse,
    scope.singular,
    scope.plural.text,
    terminology.order.singular.text,
    terminology.order.plural.text,
    terminology.customer.singular.text,
    terminology.customer.plural.text,
    terminology.delivery.plural.text,
    entity,
    installedModules,
    currentRole
  ]);

  return [ settings, setSettings ];
};

export default useCascadeSettings;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const cascadeSettingsSchema = (terminology: ITerminology) =>
  yup
    .object()
    .shape({
      minimum_order_value: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label(`Minimum ${terminology.order.singular.text} value`)
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                value: yup
                  .number()
                  .typeError(`Minimum ${terminology.order.singular.text} value must be a number`)
                  .label(`Minimum ${terminology.order.singular.text} value`)
                  .required()
              })
              .required()
          })
          .nullable()
      }),
      minimum_order_quantity: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label(`Minimum ${terminology.order.singular.text} quantity`)
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                quantity: yup
                  .number()
                  .typeError(`Minimum ${terminology.order.singular.text} quantity must be a number`)
                  .label(`Minimum ${terminology.order.singular.text} quantity`)
                  .required()
              })
              .required()
          })
          .nullable()
      }),
      one_daily_delivery: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label(`One ${terminology.order.singular.text} a day`)
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                value: yup
                  .boolean()
                  .typeError(`One ${terminology.order.singular.text} a day must be a boolean`)
                  .label(`One ${terminology.order.singular.text} a day`)
                  .required()
              })
              .required()
          })
          .nullable()
      }),
      delivery_route: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup.object()
      }),
      price_visible: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup.object()
      }),
      require_payment_method: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label("Require payment method is required")
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                value: yup
                  .boolean()
                  .typeError("Require payment method must be a boolean")
                  .label("Require payment method")
              })
              .required()
          })
          .nullable()
      }),
      allow_substitutions: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label("Substitututions option is required")
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                allowed: yup
                  .boolean()
                  .typeError("Allow substitutions must be a boolean")
                  .label("Require allow substitutions")
              })
              .required()
          })
          .nullable()
      }),
      allow_order_customer_comments: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label("Order comments option is required")
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                allowed: yup
                  .boolean()
                  .typeError("Allow must be a boolean")
                  .label("Require allow comments"),
                userGroupIds: yup
                  .array(yup.number())
              })
              .required()
          })
          .nullable()
      }),
      allow_holidays: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label("Holidays option is required")
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                allowed: yup
                  .boolean()
                  .typeError("Allow holidays must be a boolean")
                  .label("Require allow holidays")
              })
              .required()
          })
          .nullable()
      })
    })
    .required();
